import { ChevronLeft, ChevronRight } from '@mui/icons-material';
import {
    alpha,
    AppBar,
    Box,
    Card,
    CardContent,
    FormControlLabel,
    Grid,
    IconButton,
    Stack,
    Switch,
    Tab,
    Tabs,
    Theme,
    Tooltip,
    Typography,
    useMediaQuery,
    useTheme,
} from '@mui/material';
import moment from 'moment';
import React, { useState } from 'react';
import {
    OnlyFansMassDMMessageScheduleDocument,
    OnlyFansMassDMMessageScheduleDocumentWithColor,
} from '../../../../hooks/useMessageUsersMassDmList';
import useSextforceAutoRepostColors from '../../../../hooks/useSextforceAutoRepostColors';
import { OnlyFansSubscriber } from '../../../../hooks/useSubscriber';
import MessageUsersAutoMassDmCalendarDayView from './MessageUsersAutoMassDmCalendarDayView';
import MessageUsersAutoMassDmCalendarListView from './MessageUsersAutoMassDmCalendarListView';

export interface TaskOccurrence {
    task: OnlyFansMassDMMessageScheduleDocument;
    dateTime: moment.Moment;
    isRepeat: boolean;
}

type Props = {
    subscriber: OnlyFansSubscriber;
    scheduledMessages: OnlyFansMassDMMessageScheduleDocumentWithColor[] | undefined;
    scheduledMessagesLoading: boolean;
    refetchMassDmsSchedule: () => void;
    refetchMassDms: () => void;
    setMassDmScheduleInactiveStatus: (massDmScheduleId: string, inactive: boolean) => Promise<{ success: boolean }>;
    isUpdatingScheduledMessageStatus: boolean;
};

const MessageUsersAutoMassDmCalendar = (props: Props) => {
    const {
        subscriber,
        scheduledMessages,
        scheduledMessagesLoading,
        refetchMassDmsSchedule,
        refetchMassDms,
        setMassDmScheduleInactiveStatus,
        isUpdatingScheduledMessageStatus,
    } = props;
    const theme: Theme = useTheme();

    const [currentDate, setCurrentDate] = useState<moment.Moment>(
        (localStorage.getItem('messageUsersAutoMassDmCalendarViewCurrentDate') &&
            moment(localStorage.getItem('messageUsersAutoMassDmCalendarViewCurrentDate'))) ||
            moment(),
    );
    const [selectedDate, setSelectedDate] = useState<moment.Moment>(
        (localStorage.getItem('messageUsersAutoMassDmCalendarSelectedDate') &&
            moment(localStorage.getItem('messageUsersAutoMassDmCalendarSelectedDate'))) ||
            moment(),
    );
    const [view, setView] = useState<'month' | 'day' | 'list'>(
        (localStorage.getItem('messageUsersAutoMassDmCalendarView') as 'month' | 'day' | 'list') || 'month',
    );

    const [showFutureMessages, setShowFutureMessages] = useState<boolean>(
        (localStorage.getItem('messageUsersAutoMassDmCalendarShowFutureReposts') &&
            localStorage.getItem('messageUsersAutoMassDmCalendarShowFutureReposts') === 'true') ||
            false,
    );

    const { data: sextforceAutoRepostColors, isLoading: sextforceAutoRepostColorsLoading } = useSextforceAutoRepostColors();

    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

    const handlePrevMonth = () => {
        setCurrentDate(moment(currentDate).subtract(1, 'month'));
        localStorage.setItem('messageUsersAutoMassDmCalendarViewCurrentDate', moment(currentDate).subtract(1, 'month').format());
    };

    const handleNextMonth = () => {
        setCurrentDate(moment(currentDate).add(1, 'month'));
        localStorage.setItem('messageUsersAutoMassDmCalendarViewCurrentDate', moment(currentDate).add(1, 'month').format());
    };

    const handlePrevDay = () => {
        setSelectedDate(moment(selectedDate).subtract(1, 'day'));
        localStorage.setItem('messageUsersAutoMassDmCalendarSelectedDate', moment(selectedDate).subtract(1, 'day').format());

        // If the previous day is in the previous month, change the current month
        if (moment(selectedDate).subtract(1, 'day').isBefore(moment(currentDate).startOf('month'))) {
            setCurrentDate(moment(currentDate).subtract(1, 'month'));
            localStorage.setItem('messageUsersAutoMassDmCalendarViewCurrentDate', moment(currentDate).subtract(1, 'month').format());
        }
    };

    const handleNextDay = () => {
        setSelectedDate(moment(selectedDate).add(1, 'day'));
        localStorage.setItem('messageUsersAutoMassDmCalendarSelectedDate', moment(selectedDate).add(1, 'day').format());

        // If the next day is in the next month, change the current month
        if (moment(selectedDate).add(1, 'day').isAfter(moment(currentDate).endOf('month'))) {
            setCurrentDate(moment(currentDate).add(1, 'month'));
            localStorage.setItem('messageUsersAutoMassDmCalendarViewCurrentDate', moment(currentDate).add(1, 'month').format());
        }
    };

    const handleDateSelect = (date: moment.Moment) => {
        setSelectedDate(date);
        localStorage.setItem('messageUsersAutoMassDmCalendarSelectedDate', date.format());
        setView('day');
        localStorage.setItem('messageUsersAutoMassDmCalendarView', 'day');
    };

    const handleViewChange = (event: React.SyntheticEvent, newValue: any) => {
        setView(newValue);

        localStorage.setItem('messageUsersAutoMassDmCalendarView', newValue);
    };

    // Get tasks originally scheduled for a specific date
    const getOriginalScheduledMassDmsForDate = (date: moment.Moment): OnlyFansMassDMMessageScheduleDocument[] => {
        return scheduledMessages?.filter(task => moment(task.scheduleDate).isSame(date, 'day')) || [];
    };

    // Simplified and more accurate version of getAllTaskOccurrencesForDate
    const getAllTaskOccurrencesForDate = (date: moment.Moment): TaskOccurrence[] => {
        const targetDate = moment(date).startOf('day');
        const targetEndDate = moment(date).endOf('day');
        const occurrences: TaskOccurrence[] = [];

        // Add original tasks
        const originalTasks = getOriginalScheduledMassDmsForDate(date);
        originalTasks.forEach(task => {
            occurrences.push({
                task,
                dateTime: moment(task.scheduleDate),
                isRepeat: false,
            });
        });

        if (!showFutureMessages) {
            return occurrences;
        }

        // Add repeating occurrences - SIMPLIFIED VERSION
        scheduledMessages?.forEach(task => {
            if (!task.repeatEveryAmount || !task.repeatEveryUnit) return;
            if (task.repeatEndDate && targetDate.isAfter(task.repeatEndDate)) return;

            const startDateTime = moment(task.scheduleDate);

            // Skip if the target date is before the original schedule date
            if (targetEndDate.isBefore(startDateTime)) return;

            // Start from the first repeat (not the original)
            let currentRepeat = startDateTime.clone().add(task.repeatEveryAmount, task.repeatEveryUnit);

            // Keep adding intervals until we're past our target day
            while (currentRepeat.isSameOrBefore(targetEndDate.clone().add(1, 'day'))) {
                // Check if this repeat falls within our target day
                if (currentRepeat.isSameOrAfter(targetDate) && currentRepeat.isSameOrBefore(targetEndDate)) {
                    // Check if we haven't exceeded the repeat end date
                    if (!task.repeatEndDate || currentRepeat.isSameOrBefore(task.repeatEndDate)) {
                        occurrences.push({
                            task,
                            dateTime: currentRepeat.clone(),
                            isRepeat: true,
                        });
                    }
                }

                // Move to next repeat
                currentRepeat.add(task.repeatEveryAmount, task.repeatEveryUnit);

                // Safety break - don't calculate more than 100 repeats ahead
                if (currentRepeat.isAfter(moment().add(1, 'year'))) {
                    break;
                }
            }
        });

        // Sort by datetime
        return occurrences.sort((a, b) => a.dateTime.valueOf() - b.dateTime.valueOf());
    };

    // Get the actual repeat occurrence times for a single task on a specific date
    const getRepeatOccurrencesForTaskOnDate = (task: OnlyFansMassDMMessageScheduleDocument, date: moment.Moment): moment.Moment[] => {
        if (!task.repeatEveryAmount || !task.repeatEveryUnit) {
            return [];
        }

        const targetDate = moment(date).startOf('day');
        const targetEndDate = moment(date).endOf('day');

        if (task.repeatEndDate && targetDate.isAfter(task.repeatEndDate)) {
            return [];
        }

        const startDateTime = moment(task.scheduleDate);

        if (targetEndDate.isBefore(startDateTime)) {
            return [];
        }

        const occurrences: moment.Moment[] = [];
        let currentRepeat = startDateTime.clone().add(task.repeatEveryAmount, task.repeatEveryUnit);

        while (currentRepeat.isSameOrBefore(targetEndDate.clone().add(1, 'day'))) {
            if (currentRepeat.isSameOrAfter(targetDate) && currentRepeat.isSameOrBefore(targetEndDate)) {
                if (!task.repeatEndDate || currentRepeat.isSameOrBefore(task.repeatEndDate)) {
                    occurrences.push(currentRepeat.clone());
                }
            }

            currentRepeat.add(task.repeatEveryAmount, task.repeatEveryUnit);

            // Safety break
            if (currentRepeat.isAfter(moment().add(1, 'year'))) {
                break;
            }
        }

        return occurrences;
    };

    // Simplified version of getRepeatingRepostsForDate
    const getRepeatingRepostsForDate = (date: moment.Moment): { task: OnlyFansMassDMMessageScheduleDocument; count: number }[] => {
        const targetDate = moment(date).startOf('day');
        const targetEndDate = moment(date).endOf('day');

        return (
            scheduledMessages?.filter(task => {
                if (!task.repeatEveryAmount || !task.repeatEveryUnit) {
                    return false;
                }

                if (task.repeatEndDate && targetDate.isAfter(task.repeatEndDate)) {
                    return false;
                }

                const startDateTime = moment(task.scheduleDate);

                // Skip if the target date is before the original schedule date
                if (targetEndDate.isBefore(startDateTime)) return false;

                // Check if any repeat occurrence falls within this day
                let currentRepeat = startDateTime.clone().add(task.repeatEveryAmount, task.repeatEveryUnit);

                while (currentRepeat.isSameOrBefore(targetEndDate.clone().add(1, 'day'))) {
                    if (currentRepeat.isSameOrAfter(targetDate) && currentRepeat.isSameOrBefore(targetEndDate)) {
                        // Check if we haven't exceeded the repeat end date
                        if (!task.repeatEndDate || currentRepeat.isSameOrBefore(task.repeatEndDate)) {
                            return true;
                        }
                    }

                    currentRepeat.add(task.repeatEveryAmount, task.repeatEveryUnit);

                    // Safety break
                    if (currentRepeat.isAfter(moment().add(1, 'year'))) {
                        break;
                    }
                }

                return false;
            }) || []
        )
            .map(task => {
                let count = 0;
                const startDateTime = moment(task.scheduleDate);

                // Count occurrences within the target day
                let currentRepeat = startDateTime.clone().add(task.repeatEveryAmount!, task.repeatEveryUnit!);

                while (currentRepeat.isSameOrBefore(targetEndDate.clone().add(1, 'day'))) {
                    if (currentRepeat.isSameOrAfter(targetDate) && currentRepeat.isSameOrBefore(targetEndDate)) {
                        // Check if we haven't exceeded the repeat end date
                        if (!task.repeatEndDate || currentRepeat.isSameOrBefore(task.repeatEndDate)) {
                            count++;
                        }
                    }

                    currentRepeat.add(task.repeatEveryAmount!, task.repeatEveryUnit!);

                    // Safety break
                    if (currentRepeat.isAfter(moment().add(1, 'year'))) {
                        break;
                    }
                }

                return { task, count };
            })
            .filter(({ count }) => count > 0);
    };

    const MAX_TOOLTIP_MESSAGES = 8;

    // Pretty paper-style tooltip container shared by all calendar chips
    const prettyTooltipSlotProps = {
        tooltip: {
            sx: {
                backgroundColor: theme.palette.background.paper,
                color: theme.palette.text.primary,
                border: `1px solid ${theme.palette.divider}`,
                boxShadow: theme.shadows[4],
                borderRadius: `${theme.shape.borderRadius}px`,
                p: 0,
                maxWidth: 320,
            },
        },
    };

    const renderTooltipContent = (
        colorName: string,
        accentColor: string,
        subtitle: string | undefined,
        entries: { time: string; name: string }[],
    ) => {
        const visible = entries.slice(0, MAX_TOOLTIP_MESSAGES);
        const hiddenCount = entries.length - visible.length;

        return (
            <Box sx={{ p: 1.25, minWidth: 160 }}>
                <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: visible.length > 0 ? 0.75 : 0, minWidth: 0 }}>
                    <Box
                        sx={{
                            width: 10,
                            height: 10,
                            borderRadius: '50%',
                            backgroundColor: accentColor,
                            flexShrink: 0,
                        }}
                    />
                    <Typography variant="subtitle2" sx={{ fontWeight: 600, lineHeight: 1.3, minWidth: 0 }} noWrap>
                        {colorName}
                    </Typography>
                    {subtitle && (
                        <Typography variant="caption" color="text.secondary" sx={{ fontStyle: 'italic', flexShrink: 0 }}>
                            {subtitle}
                        </Typography>
                    )}
                </Stack>
                <Stack direction="column" spacing={0.25}>
                    {visible.map((entry, index) => (
                        <Stack key={index} direction="row" spacing={0.75} alignItems="baseline" sx={{ minWidth: 0 }}>
                            <Typography
                                variant="caption"
                                color="text.secondary"
                                sx={{ fontVariantNumeric: 'tabular-nums', whiteSpace: 'nowrap', flexShrink: 0 }}
                            >
                                {entry.time}
                            </Typography>
                            <Typography variant="caption" noWrap sx={{ minWidth: 0 }}>
                                {entry.name || 'Untitled message'}
                            </Typography>
                        </Stack>
                    ))}
                    {hiddenCount > 0 && (
                        <Typography variant="caption" color="text.secondary" sx={{ pt: 0.25 }}>
                            +{hiddenCount} more…
                        </Typography>
                    )}
                </Stack>
            </Box>
        );
    };

    const renderTaskCount = (tasks: OnlyFansMassDMMessageScheduleDocument[], colorId: string, isRepeating: boolean = false) => {
        const backgroundColor =
            colorId === 'none' || sextforceAutoRepostColorsLoading || !sextforceAutoRepostColors
                ? alpha(theme.palette.primary.light, 0.5)
                : sextforceAutoRepostColors.find(c => c._id === colorId)?.color || alpha(theme.palette.primary.light, 0.5);

        const colorName = sextforceAutoRepostColors?.find(c => c._id === colorId)?.name || 'No category';
        const tooltipEntries = [...tasks]
            .sort((a, b) => moment(a.scheduleDate).valueOf() - moment(b.scheduleDate).valueOf())
            .map(task => ({ time: moment(task.scheduleDate).format('LT'), name: task.name }));

        return (
            <Tooltip
                key={`${colorId}-${isRepeating}`}
                title={
                    isMobile
                        ? `${colorName}${isRepeating ? ' (Repeating)' : ''}`
                        : renderTooltipContent(colorName, backgroundColor, isRepeating ? 'Repeating' : undefined, tooltipEntries)
                }
                slotProps={isMobile ? undefined : prettyTooltipSlotProps}
            >
                <Box
                    sx={{
                        pl: 0.5,
                        pr: 0.5,
                        backgroundColor: isRepeating ? theme.palette.grey[300] : backgroundColor,
                        borderRadius: theme.shape.borderRadius,
                        width: '100%',
                        borderLeft: isRepeating ? `3px solid ${backgroundColor}` : 'none',
                    }}
                >
                    <Typography variant="caption" color={isRepeating ? backgroundColor : theme.palette.getContrastText(backgroundColor)}>
                        {tasks.length} {!isMobile && `message${tasks.length > 1 ? 's' : ''}`}
                    </Typography>
                </Box>
            </Tooltip>
        );
    };

    const renderDayContent = (day: moment.Moment) => {
        const originalTasks = getOriginalScheduledMassDmsForDate(day);
        const repeatingTasks = getRepeatingRepostsForDate(day);

        const originalGrouped = groupRepostsByColorId(originalTasks);
        const repeatingGrouped = repeatingTasks.reduce((acc, { task, count }) => {
            const colorId = task.colorId || 'none';
            if (!acc[colorId]) {
                acc[colorId] = { tasks: [], totalCount: 0 };
            }
            acc[colorId].tasks.push(task);
            acc[colorId].totalCount += count;
            return acc;
        }, {} as { [key: string]: { tasks: OnlyFansMassDMMessageScheduleDocument[]; totalCount: number } });

        return (
            <Stack direction="column" spacing={0.5} alignItems="center">
                {Object.entries(originalGrouped).map(([colorId, tasks]) => renderTaskCount(tasks, colorId))}
                {showFutureMessages &&
                    Object.entries(repeatingGrouped).map(([colorId, { tasks, totalCount }]) => {
                        const colorName = sextforceAutoRepostColors?.find(c => c._id === colorId)?.name || 'No category';
                        const accentColor =
                            colorId === 'none' || !sextforceAutoRepostColors
                                ? alpha(theme.palette.primary.light, 0.5)
                                : sextforceAutoRepostColors.find(c => c._id === colorId)?.color ||
                                  alpha(theme.palette.primary.light, 0.5);
                        const tooltipEntries = tasks
                            .flatMap(task =>
                                getRepeatOccurrencesForTaskOnDate(task, day).map(occurrence => ({
                                    at: occurrence,
                                    name: task.name,
                                })),
                            )
                            .sort((a, b) => a.at.valueOf() - b.at.valueOf())
                            .map(entry => ({ time: entry.at.format('LT'), name: entry.name }));

                        return (
                            <Tooltip
                                key={`${colorId}-repeating`}
                                title={
                                    isMobile
                                        ? `${colorName} (${tasks.length} tasks repeating ${totalCount} ${totalCount === 1 ? 'time' : 'times'})`
                                        : renderTooltipContent(colorName, accentColor, 'Repeating', tooltipEntries)
                                }
                                slotProps={isMobile ? undefined : prettyTooltipSlotProps}
                            >
                                <Box
                                    sx={{
                                        pl: 0.5,
                                        pr: 0.5,
                                        backgroundColor: theme.palette.grey[300],
                                        borderRadius: theme.shape.borderRadius,
                                        width: '100%',
                                        borderLeft: `3px solid ${accentColor}`,
                                    }}
                                >
                                    <Typography variant="caption" color={colorId === 'none' || !sextforceAutoRepostColors ? theme.palette.primary.light : accentColor}>
                                        {totalCount} {!isMobile && `repeat${totalCount > 1 ? 's' : ''}`}
                                    </Typography>
                                </Box>
                            </Tooltip>
                        );
                    })}
            </Stack>
        );
    };

    const groupRepostsByColorId = (tsaks: OnlyFansMassDMMessageScheduleDocument[]) => {
        const groupedTasks: { [key: string]: OnlyFansMassDMMessageScheduleDocument[] } = {};

        for (const task of tsaks) {
            if (!task.colorId) {
                if (!groupedTasks['none']) {
                    groupedTasks['none'] = [];
                }

                groupedTasks['none'].push(task);
                continue;
            }

            if (!groupedTasks[task.colorId]) {
                groupedTasks[task.colorId] = [];
            }

            groupedTasks[task.colorId].push(task);
        }

        return groupedTasks;
    };

    const renderMonthView = () => {
        const monthStart = moment(currentDate).startOf('month');
        const monthEnd = moment(currentDate).endOf('month');

        // Get the first day of the month (0-6, where 0 is Sunday)
        const firstDayOfMonth = monthStart.day();

        // Create array for all days including empty spots for previous month
        const days: (moment.Moment | null)[] = Array(firstDayOfMonth).fill(null);

        // Add all days of the month
        let currentDay = monthStart.clone();
        while (currentDay.isSameOrBefore(monthEnd)) {
            days.push(currentDay.clone());
            currentDay.add(1, 'day');
        }

        // Calculate remaining spots to fill out the last week
        const remainingDays = (7 - (days.length % 7)) % 7;
        days.push(...Array(remainingDays).fill(null));

        const weekDays = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

        return (
            <Box sx={{ width: '100%' }}>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                    <IconButton onClick={handlePrevMonth} disabled={moment(currentDate).isSame(moment(), 'month')}>
                        <ChevronLeft />
                    </IconButton>
                    <Typography variant="h6" sx={{ flex: 1, textAlign: 'center' }}>
                        {currentDate.format('MMMM YYYY')}
                    </Typography>
                    <IconButton onClick={handleNextMonth}>
                        <ChevronRight />
                    </IconButton>
                </Box>

                <Grid container spacing={0}>
                    {weekDays.map(day => (
                        <Grid item key={day} xs={12 / 7}>
                            <Typography align="center" variant="subtitle2">
                                {isMobile ? day.substring(0, 1) : day}
                            </Typography>
                        </Grid>
                    ))}

                    {days.map((day, index) => (
                        <Grid
                            item
                            xs={12 / 7}
                            key={index}
                            sx={{
                                borderTop: index < 7 ? `1px solid ${theme.palette.divider}` : 'none',
                                borderLeft: index % 7 !== 0 ? `1px solid ${theme.palette.divider}` : 'none',
                                borderBottom: `1px solid ${theme.palette.divider}`,
                            }}
                        >
                            {day ? (
                                <Box
                                    onClick={() => handleDateSelect(day)}
                                    sx={{
                                        p: 0.5,
                                        minHeight: 80,
                                        height: '100%',
                                        cursor: 'pointer',
                                        backgroundColor: 'background.paper',
                                        '&:hover': {
                                            backgroundColor: 'action.hover',
                                        },
                                        display: 'flex',
                                        flexDirection: 'column',
                                    }}
                                >
                                    <Typography
                                        variant="body2"
                                        align="center"
                                        sx={{
                                            fontWeight: moment().isSame(day, 'day') ? 'bold' : 'normal',
                                        }}
                                    >
                                        {day.format('D')}
                                    </Typography>
                                    {renderDayContent(day)}
                                </Box>
                            ) : (
                                <Box
                                    sx={{
                                        p: 1,
                                        minHeight: 80,
                                        height: '100%',
                                        backgroundColor: 'action.disabledBackground',
                                    }}
                                />
                            )}
                        </Grid>
                    ))}
                </Grid>
            </Box>
        );
    };

    return (
        <Box>
            <Card variant="elevation">
                <CardContent
                    sx={{
                        p: 0,
                        '&:last-child': {
                            paddingBottom: 0,
                        },
                    }}
                >
                    <AppBar position="static" sx={{ mb: 2 }}>
                        <Tabs
                            indicatorColor="secondary"
                            textColor="inherit"
                            variant="fullWidth"
                            value={view}
                            onChange={handleViewChange}
                            centered
                        >
                            <Tab label="Month" value="month" />
                            <Tab label="Day" value="day" />
                            <Tab label="List" value="list" />
                        </Tabs>
                    </AppBar>

                    {view === 'month' && renderMonthView()}
                    {view === 'day' && (
                        <MessageUsersAutoMassDmCalendarDayView
                            subscriber={subscriber}
                            taskOccurrences={getAllTaskOccurrencesForDate(selectedDate)}
                            selectedDate={selectedDate}
                            setSelectedDate={setSelectedDate}
                            handlePrevDay={handlePrevDay}
                            handleNextDay={handleNextDay}
                            refetchMassDmsSchedule={refetchMassDmsSchedule}
                            refetchMassDms={refetchMassDms}
                            setMassDmScheduleInactiveStatus={setMassDmScheduleInactiveStatus}
                            isUpdatingScheduledMessageStatus={isUpdatingScheduledMessageStatus}
                        />
                    )}
                    {view === 'list' && (
                        <MessageUsersAutoMassDmCalendarListView
                            subscriber={subscriber}
                            scheduledAutoMassDmMessage={scheduledMessages || []}
                            setSelectedDate={setSelectedDate}
                            isScheduledMessagesLoading={scheduledMessagesLoading}
                            refetchMassDmsSchedule={refetchMassDmsSchedule}
                            refetchMassDms={refetchMassDms}
                            setMassDmScheduleInactiveStatus={setMassDmScheduleInactiveStatus}
                            isUpdatingScheduledMessageStatus={isUpdatingScheduledMessageStatus}
                        />
                    )}

                    <Stack direction="column" spacing={1} sx={{ mt: 2, mb: 2 }}>
                        <FormControlLabel
                            control={
                                <Switch
                                    disabled={view === 'list'}
                                    checked={showFutureMessages}
                                    onChange={() => {
                                        setShowFutureMessages(!showFutureMessages);
                                        localStorage.setItem(
                                            'messageUsersPriorityMessageCalendarShowFutureReposts',
                                            (!showFutureMessages).toString(),
                                        );
                                    }}
                                />
                            }
                            label="Show future messages"
                            sx={{ ml: 1 }}
                        />
                    </Stack>
                </CardContent>
            </Card>
        </Box>
    );
};

export default MessageUsersAutoMassDmCalendar;
